/**
 * SeatCard — shown when a family member without a seat tries to build or start
 * talking (seat unit, operator ruling 2026-10-22). Never the paywall: the plan
 * is paid for, someone else holds the seat. Everything they made stays theirs
 * to open and play.
 *
 * A kid is told to ask a grown-up. A grown-up without a seat is offered the
 * way to change who builds (Plan & billing).
 */
import { PanelSurface } from "../chrome/PanelSurface";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";

export interface SeatCardProps {
  open: boolean;
  audience: "kid" | "grownup";
  onClose: () => void;
  /** Grown-up only: open Plan & billing to choose who builds. */
  onChooseSeats?: () => void;
}

const COPY = {
  kid: {
    title: "Ask a grown-up to unlock building",
    body: "You can still open and play everything you've made. A grown-up can turn building back on for you.",
  },
  grownup: {
    title: "Building is with someone else on this plan",
    body: "Your plan's building spots are taken. Everything you made is still here to open and play. You can choose who builds in Plan & billing.",
  },
} as const;

export function SeatCard({ open, audience, onClose, onChooseSeats }: SeatCardProps): JSX.Element | null {
  if (!open) return null;
  const copy = COPY[audience];
  const pill = {
    minHeight: 48,
    padding: "10px 22px",
    border: "none",
    borderRadius: ChromeTokens.radius.pill,
    fontFamily: ChromeTokens.font.ui,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    color: ChromeTokens.color.ink,
  } as const;
  return (
    <PanelSurface open anchor="center" onDismiss={onClose} ariaLabel={copy.title}>
      <div
        data-testid="seat-card"
        style={{
          width: "min(380px, 86vw)",
          textAlign: "center",
          fontFamily: ChromeTokens.font.ui,
          color: ChromeTokens.color.ink,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: ChromeTokens.spacing.sm,
        }}
      >
        <span
          style={{
            width: 64,
            height: 64,
            borderRadius: ChromeTokens.radius.lg,
            background: `color-mix(in srgb, var(--chrome-color-sky) 50%, var(--chrome-color-base))`,
            display: "grid",
            placeItems: "center",
          }}
        >
          <Icon name="wand-sparkles" size={30} stroke={ChromeTokens.color.clay} />
        </span>
        <h2 style={{ fontFamily: ChromeTokens.font.display, fontSize: 26, margin: 0, lineHeight: 1.1 }}>{copy.title}</h2>
        <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 15, lineHeight: 1.5, margin: 0 }}>{copy.body}</p>
        <div style={{ display: "flex", gap: ChromeTokens.spacing.sm, marginTop: ChromeTokens.spacing.sm }}>
          {audience === "grownup" && onChooseSeats && (
            <button type="button" onClick={() => { onClose(); onChooseSeats(); }} style={{ ...pill, background: ChromeTokens.color.sun }}>
              Choose who builds
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{ ...pill, background: audience === "kid" ? ChromeTokens.color.sun : ChromeTokens.color.baseMuted }}
          >
            {audience === "kid" ? "Okay" : "Not now"}
          </button>
        </div>
      </div>
    </PanelSurface>
  );
}
